import React, { useMemo } from "react";
import clsx from "clsx";

import { LESSON5_ASSESSMENT_AXIS } from "../../../../constants/active_learning";
import {
  getStudentAnswersByQuestion,
  getLessonFrom,
  getIssueFrom,
  getQuestionFrom,
} from "../../../../libs/ActiveLearning";
import {
  IActiveLearningMaterialLessonsData,
  IActiveLearningStudentAnswers,
  IActiveLearningAllAnswers,
} from "../../../../state";
import styles from "../LessonsAnswerData.module.scss";
import TreeCard from "../../../../atoms/ActiveLearning/TreeCard";

type Lesson5Pros = {
  materialCode: string;
  lessonNo: number;
  activeLearningMaterialLessons: IActiveLearningMaterialLessonsData[];
  activeLearningAnswers:
    | IActiveLearningStudentAnswers
    | IActiveLearningAllAnswers
    | null;
};

export const Lesson5: React.FC<Lesson5Pros> = ({
  materialCode,
  lessonNo,
  activeLearningMaterialLessons,
  activeLearningAnswers,
}) => {
  const activeLearningMaterialLessonsByCode = useMemo(() => {
    return activeLearningMaterialLessons.filter(
      data => data.code === materialCode,
    );
  }, [activeLearningMaterialLessons, materialCode]);

  const titleL5 = useMemo(() => {
    return getLessonFrom(activeLearningMaterialLessonsByCode, lessonNo)?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL5I1 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL5I2 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL5I2Q6 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 2, 6)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL5I2Q7 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 2, 7)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL5I3 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 3)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const answersL4I3Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      materialCode === "ECS001" ? 4 : 5,
      3,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers]);

  const answersL5I1Q1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      1,
      1,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL5I2 = useMemo(() => {
    const answersQ1 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      1,
    );
    const answersQ2 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      2,
    );
    const answersQ3 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      3,
    );
    const answersQ4 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      4,
    );
    const answersQ5 = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      5,
    );

    const allAnswers = [
      ...answersQ1,
      ...answersQ2,
      ...answersQ3,
      ...answersQ4,
      ...answersQ5,
    ];

    const result = answersL5I1Q1.map(answer => {
      const uidMatches = allAnswers.filter(
        q => q.contents.parentUid === answer.contents.uid,
      );
      return [...uidMatches, answer];
    });

    return result;
  }, [materialCode, activeLearningAnswers, answersL5I1Q1, lessonNo]);

  const answersL5I2Q6 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      6,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL5I2Q7 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      7,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL5I3Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const getAnswerFrom = (
    answers: any[],
    issueNo: number,
    questionNo: number,
  ) => {
    return (
      answers.find((answer: any) => {
        return answer.issue_no === issueNo && answer.question_no === questionNo;
      })?.contents.value || "回答データがありません。"
    );
  };

  const getAssessmentAxisAnswersFrom = (answers: any[]) => {
    return answers.filter(
      (answer: any) =>
        answer.issue_no === 2 &&
        answer.question_no >= 1 &&
        answer.question_no <= 4,
    );
  };

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo} {titleL5}
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL5I1}</p>
            <div className={styles.tree}>
              <TreeCard title={"課題"} text={answersL4I3Q1} />
              <ul className={styles.branchList}>
                {answersL5I1Q1.length > 0 ? (
                  answersL5I1Q1.map(answer => (
                    <li key={answer.id} className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>アイデア案</span>
                      </div>
                      <p className={styles.answer}>{answer.contents.value}</p>
                    </li>
                  ))
                ) : (
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>アイデア案</span>
                    </div>
                    <p className={styles.answer}>回答データがありません。</p>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          {answersL5I2.length > 0 ? (
            answersL5I2.map((answers, index) => (
              <div key={index} className={styles.question}>
                {index === 0 && <p className={styles.heading}>{titleL5I2}</p>}
                <div key={index} className={styles.tree}>
                  <p className={styles.answer}>
                    {getAnswerFrom(answers, 1, 1)}
                  </p>
                  <ul className={styles.branchList}>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>評価軸</span>
                      </div>
                      <ul className={clsx(styles.answer, styles.list)}>
                        {getAssessmentAxisAnswersFrom(answers).length > 0 ? (
                          getAssessmentAxisAnswersFrom(answers).map(
                            (answer: any, index) => (
                              <li key={index} className={styles.item}>
                                {LESSON5_ASSESSMENT_AXIS[answer.question_no]}→
                                {answer.contents.parentLabel}
                              </li>
                            ),
                          )
                        ) : (
                          <li
                            className={clsx(styles.item, styles.isContentEmpty)}
                          >
                            回答データがありません。
                          </li>
                        )}
                      </ul>
                    </li>
                    <li className={styles.branch}>
                      <div className={styles.labelWrapper}>
                        <span className={styles.label}>調べたこと</span>
                      </div>
                      <p className={clsx(styles.answer)}>
                        {getAnswerFrom(answers, 2, 5)}
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.question}>
              <p className={styles.heading}>{titleL5I2}</p>
              <div className={styles.tree}>
                <p className={styles.answer}>回答データがありません。</p>
                <ul className={styles.branchList}>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>評価軸</span>
                    </div>
                    <ul className={clsx(styles.answer, styles.list)}>
                      回答データがありません。
                    </ul>
                  </li>
                  <li className={styles.branch}>
                    <div className={styles.labelWrapper}>
                      <span className={styles.label}>調べたこと</span>
                    </div>
                    <p className={clsx(styles.answer)}>
                      回答データがありません。
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          )}
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL5I2Q6}</p>
            <p className={styles.answer}>{answersL5I2Q6}</p>
          </div>
          <div className={styles.question}>
            <p className={clsx(styles.heading, styles.dot)}>{titleL5I2Q7}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL5I2Q7}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK03</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL5I3}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL5I3Q1}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson5;
